import { css } from "@emotion/react";
import React, { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { Flex, Text } from "@phoenix/components";
import type { AnnotationSummaryGroup$key } from "@phoenix/components/annotation/__generated__/AnnotationSummaryGroup.graphql";
import { AnnotationLabel } from "@phoenix/components/annotation/AnnotationLabel";
import { AnnotationSummaryPopover } from "@phoenix/components/annotation/AnnotationSummaryPopover";
import {
  Summary,
  SummaryValue,
  SummaryValuePreview,
} from "@phoenix/pages/project/AnnotationSummary";
import type { AnnotationConfigCategorical } from "@phoenix/pages/settings/types";

import { isMirrorAnnotation } from "./annotationUtils";
import type { Annotation } from "./types";

const useAnnotationSummaryGroup = (
  span: AnnotationSummaryGroup$key,
  excludeMirrorAnnotations = false
) => {
  const data = useFragment<AnnotationSummaryGroup$key>(
    graphql`
      fragment AnnotationSummaryGroup on Span {
        project {
          id
          annotationConfigs {
            edges {
              node {
                ... on AnnotationConfigBase {
                  annotationType
                }
                ... on CategoricalAnnotationConfig {
                  id
                  name
                  optimizationDirection
                  values {
                    label
                    score
                  }
                }
              }
            }
          }
        }
        spanAnnotations {
          id
          name
          label
          score
          annotatorKind
          createdAt
          metadata
          user {
            username
            profilePictureUrl
          }
        }
        spanAnnotationSummaries {
          labelFractions {
            fraction
            label
          }
          meanScore
          name
        }
      }
    `,
    span
  );
  const { spanAnnotations, spanAnnotationSummaries } = data;
  // Online-eval run-quality verdicts are recorded on the trace *and* mirrored
  // onto the root span for display. When rendering the root span's own group,
  // callers can exclude those mirrors so the same verdict is not shown twice.
  //
  // This must be per *annotation*, not per *name*: a real (human or
  // observation-level) annotation may legitimately share a name with a mirror
  // and would otherwise disappear along with it. Only names for which every
  // annotation is a mirror are excluded.
  const mirrorOnlyAnnotationNames = useMemo(() => {
    const mirrorNames = new Set<string>();
    const nonMirrorNames = new Set<string>();
    for (const annotation of spanAnnotations) {
      if (isMirrorAnnotation(annotation)) {
        mirrorNames.add(annotation.name);
      } else {
        nonMirrorNames.add(annotation.name);
      }
    }
    for (const name of nonMirrorNames) {
      mirrorNames.delete(name);
    }
    return mirrorNames;
  }, [spanAnnotations]);
  const sortedSummariesByName = useMemo(
    () =>
      spanAnnotationSummaries
        // Note annotations are not displayed in summary groups
        .filter((summary) => summary.name !== "note")
        .filter(
          (summary) =>
            !excludeMirrorAnnotations ||
            !mirrorOnlyAnnotationNames.has(summary.name)
        )
        .sort((a, b) => {
          return a.name.localeCompare(b.name);
        }),
    [spanAnnotationSummaries, excludeMirrorAnnotations, mirrorOnlyAnnotationNames]
  );
  // newest first
  const annotationsByName = useMemo(
    () =>
      spanAnnotations.reduce(
        (acc, annotation) => {
          if (annotation.label == null && annotation.score == null) {
            return acc;
          }
          if (!acc[annotation.name]) {
            acc[annotation.name] = [annotation];
          } else {
            acc[annotation.name] = [annotation, ...acc[annotation.name]].sort(
              (a, b) => {
                return (
                  new Date(b.createdAt).getTime() -
                  new Date(a.createdAt).getTime()
                );
              }
            );
          }
          return acc;
        },
        {} as Record<string, typeof spanAnnotations>
      ),
    [spanAnnotations]
  );
  const categoricalAnnotationConfigsByName = useMemo(() => {
    return data.project.annotationConfigs.edges.reduce(
      (acc, edge) => {
        const name = edge.node.name;
        if (name && edge.node.annotationType === "CATEGORICAL") {
          acc[name] = edge.node as AnnotationConfigCategorical;
        }
        return acc;
      },
      {} as Record<string, AnnotationConfigCategorical>
    );
  }, [data.project.annotationConfigs]);
  return {
    sortedSummariesByName,
    annotationsByName,
    categoricalAnnotationConfigsByName,
  };
};

type AnnotationSummaryGroupProps = {
  span: AnnotationSummaryGroup$key;
  showFilterActions?: boolean;
  renderEmptyState?: () => React.ReactNode;
  excludeMirrorAnnotations?: boolean;
};

const annotationLabelCSS = css`
  min-height: 20px;
  align-items: center;
  justify-content: center;
  display: flex;
`;

export const AnnotationSummaryGroupTokens = ({
  span,
  showFilterActions = false,
  renderEmptyState,
  excludeMirrorAnnotations = false,
}: AnnotationSummaryGroupProps) => {
  const {
    sortedSummariesByName,
    annotationsByName,
    categoricalAnnotationConfigsByName,
  } = useAnnotationSummaryGroup(span, excludeMirrorAnnotations);

  if (sortedSummariesByName.length === 0 && renderEmptyState) {
    return renderEmptyState();
  }

  return (
    <Flex direction="row" gap="size-50" wrap="wrap">
      {sortedSummariesByName.map((summary) => {
        const latestAnnotation = annotationsByName[summary.name]?.[0];
        const meanScore = summary?.meanScore;
        if (!latestAnnotation) {
          return null;
        }
        return (
          <AnnotationSummaryPopover
            key={latestAnnotation.id}
            annotations={annotationsByName[summary.name]}
            width="500px"
            meanScore={meanScore}
            showFilterActions={showFilterActions}
          >
            <AnnotationLabel
              annotation={latestAnnotation}
              annotationDisplayPreference="none"
              css={annotationLabelCSS}
              clickable
            >
              {meanScore != null ? (
                <SummaryValuePreview
                  name={latestAnnotation.name}
                  meanScore={meanScore}
                  size="S"
                  disableAnimation
                  annotationConfig={
                    categoricalAnnotationConfigsByName[latestAnnotation.name]
                  }
                />
              ) : null}
            </AnnotationLabel>
          </AnnotationSummaryPopover>
        );
      })}
    </Flex>
  );
};

export const AnnotationSummaryGroupStacks = ({
  span,
  renderEmptyState,
  excludeMirrorAnnotations = false,
  heading,
}: AnnotationSummaryGroupProps & { heading?: string }) => {
  const {
    sortedSummariesByName,
    annotationsByName,
    categoricalAnnotationConfigsByName,
  } = useAnnotationSummaryGroup(span, excludeMirrorAnnotations);

  if (sortedSummariesByName.length === 0 && renderEmptyState) {
    return renderEmptyState();
  }
  return (
    <Flex
      direction="column"
      gap="size-100"
      css={css`
        align-items: flex-start;
        min-width: 0;
        flex: 1 1 auto;
        max-width: 100%;
        overflow: hidden;
      `}
    >
      {heading ? (
        <Text elementType="h3" size="S" color="text-700">
          {heading}
        </Text>
      ) : null}
      <div
        css={css`
          max-width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          padding-bottom: 6px;
          scrollbar-gutter: stable;
          scrollbar-width: thin;
          scrollbar-color: #64748b #e2e8f0;

          &::-webkit-scrollbar {
            height: 8px !important;
            display: block !important;
          }
          &::-webkit-scrollbar-track {
            background: #e2e8f0 !important;
            border-radius: 4px;
          }
          &::-webkit-scrollbar-thumb {
            background-color: #64748b !important;
            border-radius: 4px;
          }
          &::-webkit-scrollbar-thumb:hover {
            background-color: #334155 !important;
          }
        `}
      >
        <Flex direction="row" gap="size-400">
          {sortedSummariesByName.map((summary) => {
            const latestAnnotation = annotationsByName[summary.name]?.[0];
            if (!latestAnnotation) {
              return null;
            }
            return (
              <Summary name={latestAnnotation.name} key={latestAnnotation.id}>
                <SummaryValue
                  name={latestAnnotation.name}
                  meanScore={summary.meanScore}
                  labelFractions={summary.labelFractions}
                  annotationConfig={
                    categoricalAnnotationConfigsByName[latestAnnotation.name]
                  }
                />
              </Summary>
            );
          })}
        </Flex>
      </div>
    </Flex>
  );
};

type TraceAnnotationSummary = {
  name: string;
  meanScore?: number | null;
  labelFractions: readonly { label: string; fraction: number }[];
};

type TraceAnnotationSummaryGroupTokensProps = {
  /**
   * The run-level (trace) annotations to render. These are the canonical
   * per-run verdicts and take precedence over step/observation-level ones.
   */
  annotations: readonly Annotation[];
  summaries: readonly TraceAnnotationSummary[];
  showFilterActions?: boolean;
  renderEmptyState?: () => React.ReactNode;
  annotationConfigsByName?: Record<string, AnnotationConfigCategorical>;
};

/**
 * Renders run-level (trace) annotation tokens from already-fetched data.
 *
 * Unlike {@link AnnotationSummaryGroupTokens}, this does not read from a GraphQL
 * fragment so it can be used in tables that fetch trace annotations inline
 * (e.g. the project traces table) without a query per row.
 */
export const TraceAnnotationSummaryGroupTokens = ({
  annotations,
  summaries,
  showFilterActions = false,
  renderEmptyState,
  annotationConfigsByName = {},
}: TraceAnnotationSummaryGroupTokensProps) => {
  const sortedSummariesByName = useMemo(
    () =>
      summaries
        // Note annotations are not displayed in summary groups
        .filter((summary) => summary.name !== "note")
        .sort((a, b) => a.name.localeCompare(b.name)),
    [summaries]
  );
  // newest first
  const annotationsByName = useMemo(
    () =>
      annotations.reduce(
        (acc, annotation) => {
          if (annotation.label == null && annotation.score == null) {
            return acc;
          }
          acc[annotation.name] = acc[annotation.name]
            ? [...acc[annotation.name], annotation].sort((a, b) => {
                return (
                  new Date(b.createdAt ?? 0).getTime() -
                  new Date(a.createdAt ?? 0).getTime()
                );
              })
            : [annotation];
          return acc;
        },
        {} as Record<string, Annotation[]>
      ),
    [annotations]
  );

  if (sortedSummariesByName.length === 0 && renderEmptyState) {
    return renderEmptyState();
  }

  return (
    <Flex direction="row" gap="size-50" wrap="wrap">
      {sortedSummariesByName.map((summary) => {
        const latestAnnotation = annotationsByName[summary.name]?.[0];
        const meanScore = summary?.meanScore;
        if (!latestAnnotation) {
          return null;
        }
        return (
          <AnnotationSummaryPopover
            key={`trace-${latestAnnotation.id}`}
            annotations={annotationsByName[summary.name]}
            width="500px"
            meanScore={meanScore}
            showFilterActions={showFilterActions}
          >
            <AnnotationLabel
              annotation={latestAnnotation}
              annotationDisplayPreference="none"
              css={annotationLabelCSS}
              clickable
            >
              {meanScore != null ? (
                <SummaryValuePreview
                  name={latestAnnotation.name}
                  meanScore={meanScore}
                  size="S"
                  disableAnimation
                  annotationConfig={annotationConfigsByName[latestAnnotation.name]}
                />
              ) : null}
            </AnnotationLabel>
          </AnnotationSummaryPopover>
        );
      })}
    </Flex>
  );
};
